import { ColumnTab } from 'src/features/well-form/entities/column-tab.entity';
import { FormStore, IFormEvents } from 'src/features/well-form/entities/form.entity';
import { getDefaultFormPlugins, FormPlugin } from 'src/features/well-form/plugins';
import { Item, Tab } from 'src/shared/entities/abstract-control-entities';
import { hasValue } from 'src/shared/utils/common';
import { EventBus } from 'src/shared/utils/event-bus';
import { parsePrimitivesFromString } from 'src/shared/utils/parse-primitives-from-string';
import { RootStore } from 'src/store';
import { Directories } from 'src/store/directories/directories.store';

import { TSavedFormItems, TSavedFormValue, TColumnRaw, TColumnTabRaw, TItemRaw, TTabRaw } from '../types';

import { mapComboBoxItem } from './combobox-serializers/map-combobox';
import { mapMulticombobox } from './combobox-serializers/map-multicombobox';
import { mapTreeMultiCombobox } from './combobox-serializers/map-treemulticombobox';
import { mapFieldItem } from './field-serializers/map-field-item';
import { mapDateIntervalFieldItem } from './interval-field-serializers/map-date-interval-field.serializer';
import { mapPassingIntervalFieldItem } from './interval-field-serializers/map-passing-interval-field.serializer';
import { mapLabelItem } from './map-label-item';
import { mapSwitchItem } from './map-switch-item';
import { mapTextArea } from './map-text-area';

export const mapData = (
  rootStore: RootStore,
  initialValues: Record<string, unknown> | null,
  plugins: FormPlugin[] = [],
): FormStore => {
  return new FormStore(rootStore, initialValues, [...getDefaultFormPlugins(rootStore), ...plugins]);
};

export const mapTabs = (
  data: TTabRaw[],
  savedForm: Record<string, unknown> | null,
  directories: Directories,
  eventBus: EventBus<IFormEvents>,
): Tab[] => {
  const tempFilteredData = data.filter((tab) => tab.fieldId !== 'geologicalLoad' && tab.fieldId !== 'stages');

  return tempFilteredData.map((tab) => {
    // Временно отключенный функционалл восстановления формы из локального хранилища
    // if (tab.kind === 'tabWithStages') return mapApproachesList(tab, directories, savedItems);
    // if (tab.kind === 'tabWithStages') {
    //   // const savedItems = (() => {
    //   //   const rawData = savedForm?.['GOplan_RigOperations'];

    //   //   return Array.isArray(rawData) ? (rawData as TSerializedRigOperation[]) : undefined;
    //   // })();

    //   // return mapApproachesList(tab, directories, eventBus, savedItems);
    // }
    // return mapColumnTab(tab, directories, savedItems);
    return mapColumnTab(tab as TColumnTabRaw, directories, savedForm ?? undefined);
  });
};

const mapColumnTab = (tab: TColumnTabRaw, directories: Directories, savedItems?: TSavedFormItems): ColumnTab => {
  const columns = tab.columns.map((column: TColumnRaw, index: number) => {
    return { id: index + 1, items: mapItems(column.items, directories, savedItems) };
  });

  return new ColumnTab({ formElementRefId: tab.fieldId, columns });
};

export const mapItems = (items: TItemRaw[], directories: Directories, savedItems?: TSavedFormItems): Item[] => {
  return items.map((item) => {
    const savedValue = (() => {
      if (!savedItems) {
        return undefined;
      }

      if (Array.isArray(savedItems)) {
        return undefined;
      }

      const valueByAttrName = item.attrName ? savedItems[item.attrName] : undefined;
      const valueByFieldId = savedItems[item.fieldId];

      return valueByAttrName ?? valueByFieldId;
    })();

    return mapItem(item, directories, savedValue);
  });
};

export const mapItem = (item: TItemRaw, directories: Directories, savedValue?: TSavedFormValue): Item => {
  const parsedValue = (() => {
    if (!hasValue(savedValue)) {
      return null;
    }

    if (typeof savedValue === 'object') {
      return savedValue;
    }

    return parsePrimitivesFromString(savedValue as string);
  })();

  switch (item.control) {
    case 'Field':
      return mapFieldItem({ item, directories, savedValue: parsedValue });
    case 'TextArea':
      return mapTextArea({ item, directories, savedValue: parsedValue });
    case 'ComboBox':
      return mapComboBoxItem({ item, directories, savedValue: parsedValue });
    case 'MultiComboBox':
      return mapMulticombobox({ item, directories, savedValue: parsedValue });
    case 'TreeMultiComboBox':
      return mapTreeMultiCombobox({ item, directories });
    case 'PassingInterval':
      return mapPassingIntervalFieldItem({ item, directories, savedValue: parsedValue });
    case 'DateTimeIntervalWithDuration':
      return mapDateIntervalFieldItem({ item, savedValue: parsedValue, directories });
    case 'CheckBox':
      return mapSwitchItem({ item, directories, savedValue: parsedValue });
    case 'Label': {
      const directory = item.refObjectType ? directories.getObject(item.refObjectType) : null;
      return mapLabelItem({ item, directories, savedValue: parsedValue, directory });
    }
    default: {
      console.error('Unable to recognize the item', item);
      throw new Error('Unable to recognize the item');
    }
  }
};
